import { Car, Users, Target, Award, Globe, Heart } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-800 to-gray-900 text-white py-20">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl sa-gradient flex items-center justify-center">
              <Car className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">About AutoSearchSA</h1>
            <p className="text-xl text-gray-300">
              South Africa's car buying decision engine. Empowering buyers, connecting partners.
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              AutoSearchSA exists to make buying a used car in South Africa simpler, safer, and smarter. 
              We believe every buyer deserves access to transparent pricing, independent inspections, 
              and fair finance – all in one place.
            </p>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">What We Do</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-xl flex items-center justify-center">
                <Globe className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Aggregate</h3>
              <p className="text-gray-600">
                We pull listings from multiple dealers and platforms across South Africa, 
                giving you one place to compare thousands of vehicles.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-xl flex items-center justify-center">
                <Target className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Connect</h3>
              <p className="text-gray-600">
                We connect you with trusted inspection services, finance providers, 
                and insurers so your entire buying journey is covered.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-purple-100 rounded-xl flex items-center justify-center">
                <Award className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Educate</h3>
              <p className="text-gray-600">
                Through guides, FAQs, and tools like our finance calculator, 
                we help you make informed decisions, not emotional ones.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* For Buyers */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">For Car Buyers</h2>
              <div className="space-y-4">
                {[
                  'Search once, see results from multiple dealers',
                  'Compare prices to find the best deals',
                  'Book independent inspections for peace of mind',
                  'Get pre-approved for finance at competitive rates',
                  'Find insurance and life cover all in one place',
                  'No pressure, no pushy salespeople – you\'re in control'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-green-600 text-sm">✓</span>
                    </div>
                    <span className="text-gray-600">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">AutoSearchSA is not a dealer</h3>
              <p className="text-blue-100 mb-6">
                We don't sell cars. We don't take commissions from dealers for pushing specific vehicles. 
                Our job is to help you find the best car, not the one that makes us the most money.
              </p>
              <p className="text-blue-100">
                We make money when you use our partner services – inspections, finance, insurance – 
                but only if they genuinely help you. That's the model that keeps us honest.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* For Partners */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 bg-gray-900 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Quality Leads, Not Just Traffic</h3>
              <p className="text-gray-300 mb-6">
                Users who come through AutoSearchSA are actively searching for cars and services. 
                They're not casual browsers – they're purchase-ready.
              </p>
              <p className="text-gray-300">
                Our tracking and analytics help you understand exactly what users are looking for, 
                so you can serve them better.
              </p>
            </div>
            
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">For Partners</h2>
              <div className="space-y-4">
                {[
                  'Receive qualified, purchase-ready leads',
                  'Pay only for results, not advertising impressions',
                  'Full click and conversion tracking',
                  'No complex marketing setup required',
                  'Scale nationally without more sales staff',
                  'Join a trusted ecosystem of quality providers'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-600 text-sm">✓</span>
                    </div>
                    <span className="text-gray-600">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-white">
        <div className="container">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Our Values</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                icon: Users,
                title: 'Buyer First',
                desc: 'Every decision we make starts with "does this help the buyer?" If not, we don\'t do it.'
              },
              {
                icon: Target,
                title: 'Transparency',
                desc: 'No hidden fees, no paid placements, no misleading information. What you see is what you get.'
              },
              {
                icon: Heart,
                title: 'Integrity',
                desc: 'We only partner with services we would use ourselves. Our reputation depends on yours.'
              },
              {
                icon: Award,
                title: 'Excellence',
                desc: 'We aim to be the best car search experience in South Africa, bar none.'
              }
            ].map((value, idx) => (
              <div key={idx} className="flex items-start gap-4 p-6 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <value.icon className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">{value.title}</h3>
                  <p className="text-gray-600 text-sm">{value.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
